import { Link } from '@inertiajs/react'
import { ArrowUpRight } from 'lucide-react'

/* The console, as a wireframe rather than a screenshot.
 *
 * The Distribution page used to open with two full product mocks stacked on
 * top of each other, which made it enormously tall before a reader had reached
 * a single capability. A wireframe answers the question those mocks were
 * answering — "what am I actually getting" — in a fraction of the height, and
 * the two screens worth seeing in full now have pages of their own.
 *
 * DELIBERATELY A WIREFRAME, not a low-fidelity screenshot. It is line-work and
 * labels: nobody can mistake it for a render, so it makes no promises about
 * colour, type or spacing that the product then has to keep. The screens it
 * names are real, and the two that link out lead to their full mock.
 *
 * The labelled regions are the console's actual shape — a grouped rail, a
 * header, and a work area — so the diagram teaches the layout a prospect will
 * be looking at during a demo.
 */

interface Region {
  key: string
  label: string
  note: string
  /** Detail pages exist for these two; the rest are labels only. */
  href?: string
}

const RAIL = [
  { group: 'Catalog', items: ['Releases', 'Artists', 'Composers', 'Labels'] },
  { group: 'Delivery', items: ['Stores', 'Deliveries', 'Takedowns'] },
  { group: 'Money', items: ['Statements', 'Analytics', 'Payouts', 'Invoices'] },
  { group: 'Clients', items: ['Accounts', 'Team', 'API keys'] },
  { group: 'Support', items: ['Tickets', 'Forms'] },
]

const REGIONS: Region[] = [
  {
    key: 'builder',
    label: 'Release builder',
    note: 'Three tabs, autosave, validation panel',
    href: '/technology/platform/release-builder',
  },
  {
    key: 'analytics',
    label: 'Royalty analytics',
    note: 'Revenue, units, RPM, drill-down',
    href: '/technology/platform/analytics',
  },
  { key: 'queue', label: 'Review queue', note: 'Approve, return with a reason, or hold' },
  { key: 'payouts', label: 'Payout run', note: 'Threshold-triggered, multi-currency' },
  { key: 'stores', label: 'Store manager', note: 'Per-account catalogue of destinations' },
  { key: 'tickets', label: 'Support desk', note: 'Threaded email, SLA clocks, shared mailboxes' },
]

export default function PlatformWireframe() {
  return (
    <div className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-5 sm:p-8">
      <div className="mb-7 text-center">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-[#ff8c4d]">
          The shape of it
        </p>
        <h3
          className="mx-auto mt-2 max-w-xl text-lg font-semibold leading-snug text-white sm:text-xl"
          style={{ fontFamily: 'var(--wavbee-font-display)' }}
        >
          One console, and everything a distributor runs inside it.
        </h3>
      </div>

      <div className="overflow-hidden rounded-xl border border-dashed border-white/[0.14] bg-black/25">
        <div className="flex">
          {/* Rail — grouped, exactly as the console groups it. */}
          <div className="hidden w-[150px] shrink-0 border-r border-dashed border-white/[0.14] p-3 sm:block">
            <div className="mb-3 flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm border border-white/25" aria-hidden="true" />
              <span className="h-1.5 w-14 rounded-full bg-white/15" aria-hidden="true" />
            </div>
            <div className="flex flex-col gap-3">
              {RAIL.map((section) => (
                <div key={section.group}>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/30">
                    {section.group}
                  </p>
                  <div className="flex flex-col gap-1">
                    {section.items.map((item) => (
                      <span key={item} className="text-[11px] text-white/45">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Work area. */}
          <div className="min-w-0 flex-1 p-3">
            <div className="mb-3 flex items-center justify-between gap-3 border-b border-dashed border-white/[0.14] pb-3">
              <span className="h-1.5 w-24 rounded-full bg-white/15" aria-hidden="true" />
              <span className="flex items-center gap-1.5" aria-hidden="true">
                <span className="h-1.5 w-10 rounded-full bg-white/10" />
                <span className="h-4 w-4 rounded-full border border-white/20" />
              </span>
            </div>

            <div className="grid gap-2.5 sm:grid-cols-2">
              {REGIONS.map((region) => {
                const body = (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[13px] font-semibold text-white/85">{region.label}</p>
                      {region.href && (
                        <ArrowUpRight
                          className="h-3.5 w-3.5 shrink-0 text-white/30 transition-colors group-hover:text-[#ff6a1a]"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                    <p className="mt-1 text-[11px] leading-relaxed text-white/40">{region.note}</p>
                    {/* Two rules and a block, standing in for content. A
                        wireframe should look like a wireframe. */}
                    <span className="mt-3 flex flex-col gap-1.5" aria-hidden="true">
                      <span className="h-1.5 w-full rounded-full bg-white/[0.07]" />
                      <span className="h-1.5 w-4/5 rounded-full bg-white/[0.07]" />
                      <span className="h-1.5 w-2/3 rounded-full bg-white/[0.07]" />
                    </span>
                    {region.href && (
                      <span className="mt-3 inline-block text-[11px] font-medium text-[#ff8c4d]">
                        See this screen →
                      </span>
                    )}
                  </>
                )

                return region.href ? (
                  <Link
                    key={region.key}
                    href={region.href}
                    prefetch
                    className="group rounded-lg border border-dashed border-white/[0.16] p-3.5 no-underline transition-all hover:border-[#ff6a1a]/40 hover:bg-[#ff6a1a]/[0.04]"
                  >
                    {body}
                  </Link>
                ) : (
                  <div
                    key={region.key}
                    className="rounded-lg border border-dashed border-white/[0.12] p-3.5"
                  >
                    {body}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      <p className="mx-auto mt-5 max-w-2xl text-center text-[0.75rem] leading-relaxed text-white/45">
        A wireframe, on purpose — it makes no promises about colour or spacing that a screenshot
        would. Two of these screens carry enough to be worth seeing in full; the links open them.
      </p>
    </div>
  )
}
